import React, { useRef, useEffect } from 'react';
import './TrendingItemsComponent.css';
import SafeImage from './common/SafeImage';
import { useTranslation } from '../redux/TranslationHelper';

const TrendingItemsComponent = ({ 
  id, 
  style = {}, 
  componentProps = {}, 
  isPreview = false,
  viewMode = 'pc'
}) => {
  const { t } = useTranslation();
  
  // 기본 속성 추출
  const { 
    className = "trending-items-component",
    title = t("Trending now"),
    subtitle = t("에디터가 선정한"),
    is_logged = true,
    is_not_logged = true,
    imageRadius = "4px",
    // MORE 버튼 관련 속성 추가
    showMoreButton = true,
    moreButtonText = t("MORE"),
    moreButtonUrl = "#",
    // MORE 버튼 색상 속성 추가
    moreButtonTextColor = "#8a7a6d",
    moreButtonBorderColor = "#8a7a6d",
    moreButtonBgColor = "transparent",
    moreButtonHoverTextColor = "#ffffff",
    moreButtonHoverBgColor = "#8a7a6d",
    items = [
      {
        id: 1,
        image: "https://via.placeholder.com/400x400",
        title: t("민감성 피부 스킨&케어"),
        url: "#"
      },
      {
        id: 2,
        image: "https://via.placeholder.com/400x400",
        title: t("수분 마스크팩 듀오 할인"),
        url: "#"
      },
      {
        id: 3,
        image: "https://via.placeholder.com/400x400",
        title: t("저자극 버블 클렌저 기획전"),
        url: "#"
      }
    ]
  } = componentProps || {};

  // 컴포넌트 고유 ID 생성
  const componentUniqueId = useRef(`trending-items-${id || Math.random().toString(36).substr(2, 9)}`);
  
  // 호버 스타일 동적 생성 및 적용
  useEffect(() => {
    // 기존 스타일 태그 제거
    const existingStyle = document.getElementById(`hover-style-${componentUniqueId.current}`);
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // 새 스타일 태그 생성
    const styleEl = document.createElement('style');
    styleEl.id = `hover-style-${componentUniqueId.current}`;
    styleEl.innerHTML = `
      .${componentUniqueId.current} .trending-items-more-button:hover {
        background-color: ${moreButtonHoverBgColor || '#8a7a6d'} !important;
        color: ${moreButtonHoverTextColor || '#ffffff'} !important;
      }
    `;
    document.head.appendChild(styleEl);
    
    // 컴포넌트 언마운트 시 스타일 태그 제거
    return () => {
      const styleToRemove = document.getElementById(`hover-style-${componentUniqueId.current}`);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [moreButtonHoverBgColor, moreButtonHoverTextColor, id]);

  // 디바이스별 속성 적용
  const isMobile = viewMode === 'mobile' || viewMode === 'tablet';
  const currentStyles = isMobile 
    ? componentProps?.styles?.mobile 
    : componentProps?.styles?.pc;

  // 아이템 컨텐츠 렌더링
  const renderItems = () => {
    return (items || []).map((item, index) => {
      // URL이 유효한지 확인 (빈 문자열, #, undefined, null이 아닌 경우)
      const hasValidUrl = item.url && item.url !== "#" && item.url.trim() !== "";
      
      // 아이템 제목에 다국어 처리 적용
      const translatedTitle = typeof item.title === 'string' ? t(item.title) : item.title;
      
      // 아이템 내용 (동일한 내용을 a 태그 또는 div로 감쌀 예정)
      const itemContent = (
        <>
          <div 
            className="trending-item-image-container" 
            style={{ borderRadius: imageRadius }}
          >
            <SafeImage 
              src={item.image} 
              alt={translatedTitle}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                borderRadius: imageRadius
              }}
              containerStyle={{ 
                width: '100%', 
                aspectRatio: '1/1',
                borderRadius: imageRadius
              }}
            />
          </div>
          <div className="trending-item-content">
            <h3 className="trending-item-title">{translatedTitle}</h3>
            {/* <div className="trending-item-more">more</div> */}
          </div>
        </>
      );
      
      return (
        <div key={item.id || index} className="trending-item">
          {hasValidUrl ? (
            // 유효한 URL이 있으면 a 태그 사용
            <a href={item.url} className="trending-item-link">
              {itemContent}
            </a>
          ) : (
            // 유효한 URL이 없으면 div 태그 사용
            <div className="trending-item-link">
              {itemContent}
            </div>
          )}
        </div>
      );
    });
  };

  // MORE 버튼 URL이 유효한지 확인
  const hasValidMoreUrl = moreButtonUrl && moreButtonUrl !== "#" && moreButtonUrl.trim() !== "";

  // 버튼 스타일 정의
  const moreButtonStyle = {
    color: moreButtonTextColor || "#8a7a6d",
    borderColor: moreButtonBorderColor || "#8a7a6d",
    backgroundColor: moreButtonBgColor || "transparent"
  };

  // 최종 컴포넌트 클래스명
  const finalClassName = `${className} ${componentUniqueId.current}`;

  // MORE 버튼 텍스트에 다국어 처리 적용
  const translatedMoreButtonText = typeof moreButtonText === 'string' ? t(moreButtonText) : moreButtonText;

  return (
    <div id={id} className={finalClassName} style={{
      ...style,
      ...(currentStyles || {})
    }}>
      <div className="trending-items-header">
        <div className="trending-items-subtitle">{t(subtitle)}</div>
        <h2 className="trending-items-title">{t(title)}</h2>
      </div>
      <div className="trending-items-container">
        {renderItems()}
      </div>
      
      {/* MORE 버튼 */}
      {showMoreButton && (
        <div className="trending-items-more-button-container">
          {hasValidMoreUrl ? (
            <a 
              href={moreButtonUrl} 
              className="trending-items-more-button"
              style={moreButtonStyle}
            >
              {translatedMoreButtonText}
            </a>
          ) : (
            <button 
              className="trending-items-more-button"
              style={moreButtonStyle}
            >
              {translatedMoreButtonText}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TrendingItemsComponent; 